import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../api/client.js';
import type { NotificationsResponse } from '../api/types.js';
import { Modal } from './Modal.js';
import { Button, EmptyState, Spinner, classes } from './ui.js';
import { formatDateTime } from '../lib/format.js';

export function NotificationPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', 'panel'],
    queryFn: () =>
      apiRequest<NotificationsResponse>('/api/notifications', { query: { limit: 50 } }),
    enabled: open,
  });

  const markRead = useMutation({
    mutationFn: (payload: { notificationIds?: string[]; all?: boolean }) =>
      apiRequest('/api/notifications/mark-read', { method: 'POST', body: payload }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const notifications = data?.notifications ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Notifications"
      size="lg"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => markRead.mutate({ all: true })}
            disabled={(data?.unreadCount ?? 0) === 0}
            loading={markRead.isPending}
          >
            Mark all as read
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      {isLoading ? (
        <Spinner label="Loading notifications" />
      ) : notifications.length === 0 ? (
        <EmptyState
          title="No notifications"
          description="Updates on your work orders will appear here."
        />
      ) : (
        <ul className="divide-y divide-slate-200">
          {notifications.map((notification) => (
            <li key={notification.id}>
              <button
                type="button"
                className={classes(
                  'flex w-full flex-col gap-1 px-1 py-3 text-left hover:bg-slate-50',
                  !notification.readAt && 'bg-brand-50/50',
                )}
                onClick={() => {
                  if (!notification.readAt) {
                    markRead.mutate({ notificationIds: [notification.id] });
                  }
                  if (notification.workOrderId) {
                    onClose();
                    navigate(`/work-orders/${notification.workOrderId}`);
                  }
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="text-sm font-medium text-slate-900">{notification.title}</span>
                  {!notification.readAt && (
                    <span
                      className="mt-1 size-2 shrink-0 rounded-full bg-brand-600"
                      aria-label="Unread"
                    />
                  )}
                </div>
                {notification.body && <p className="text-sm text-slate-600">{notification.body}</p>}
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  {notification.workOrderNumber && <span>{notification.workOrderNumber}</span>}
                  <span>{formatDateTime(notification.createdAt)}</span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
